import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faCropAlt,
  faRotateRight,
  faUndo,
  faCheck,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

const COLORS = [
  { id: 'red', hex: '#ef4444', label: 'Rojo' },
  { id: 'yellow', hex: '#eab308', label: 'Amarillo' },
  { id: 'green', hex: '#22c55e', label: 'Verde' },
  { id: 'blue', hex: '#3b82f6', label: 'Azul' },
  { id: 'white', hex: '#ffffff', label: 'Blanco' },
  { id: 'black', hex: '#000000', label: 'Negro' }
];

const STROKE_WIDTHS = [
  { id: 'fine', size: 3, label: 'Fino' },
  { id: 'medium', size: 6, label: 'Medio' },
  { id: 'thick', size: 12, label: 'Grueso' }
];

const ImageEditorModal = ({
  isOpen,
  filePreview,
  onSave,
  onClose,
  isDark
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [activeTool, setActiveTool] = useState('pen'); // 'pen' | 'highlighter' | 'crop'
  const [strokeColor, setStrokeColor] = useState('#ef4444');
  const [strokeWidth, setStrokeWidth] = useState(6);
  const [isDrawing, setIsDrawing] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  // Selección de recorte
  const [cropSelection, setCropSelection] = useState(null); // { x, y, width, height }
  const [cropDragStart, setCropDragStart] = useState(null);

  // Inicializar canvas con la imagen original
  useEffect(() => {
    if (!isOpen || !filePreview?.url) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });

      const initialData = canvas.toDataURL('image/png');
      setHistory([initialData]);
      setHistoryIndex(0);
      setCropSelection(null);
      setActiveTool('pen');
    };
    img.src = filePreview.url;
  }, [isOpen, filePreview?.url]);

  // Guardar estado actual en el historial
  const pushHistoryState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL('image/png');
    setHistory(prev => [...prev.slice(0, historyIndex + 1), data]);
    setHistoryIndex(prev => prev + 1);
  }, [historyIndex]);

  // Deshacer acción
  const handleUndo = () => {
    if (historyIndex <= 0) return;
    const targetIndex = historyIndex - 1;
    const targetData = history[targetIndex];

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      setHistoryIndex(targetIndex);
      setCropSelection(null);
    };
    img.src = targetData;
  };

  // Restablecer a original
  const handleReset = () => {
    if (history.length === 0) return;
    const initialData = history[0];

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
      setHistory([initialData]);
      setHistoryIndex(0);
      setCropSelection(null);
    };
    img.src = initialData;
  };

  // Rotar 90 grados en sentido horario
  const handleRotate90 = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.height;
    tempCanvas.height = canvas.width;
    const tempCtx = tempCanvas.getContext('2d');

    tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
    tempCtx.rotate((90 * Math.PI) / 180);
    tempCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

    canvas.width = tempCanvas.width;
    canvas.height = tempCanvas.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(tempCanvas, 0, 0);
    setImageDimensions({ width: canvas.width, height: canvas.height });
    setCropSelection(null);
    pushHistoryState();
  };

  // Convertir coordenadas de evento a coordenadas nativas del canvas
  const getCanvasCoords = e => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX !== undefined ? e.clientX : (e.touches?.[0]?.clientX || 0);
    const clientY = e.clientY !== undefined ? e.clientY : (e.touches?.[0]?.clientY || 0);
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: Math.max(0, Math.min(canvas.width, (clientX - rect.left) * scaleX)),
      y: Math.max(0, Math.min(canvas.height, (clientY - rect.top) * scaleY))
    };
  };

  // Inicio de trazo o inicio de selección de recorte
  const handleMouseDown = e => {
    const coords = getCanvasCoords(e);

    if (activeTool === 'crop') {
      setCropDragStart(coords);
      setCropSelection({ x: coords.x, y: coords.y, width: 0, height: 0 });
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (activeTool === 'highlighter') {
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth * 3.5;
      ctx.globalAlpha = 0.42;
    } else {
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.globalAlpha = 1.0;
    }
  };

  // Movimiento de trazo o expansión de recorte
  const handleMouseMove = e => {
    const coords = getCanvasCoords(e);

    if (activeTool === 'crop' && cropDragStart) {
      const x = Math.min(cropDragStart.x, coords.x);
      const y = Math.min(cropDragStart.y, coords.y);
      const width = Math.abs(coords.x - cropDragStart.x);
      const height = Math.abs(coords.y - cropDragStart.y);
      setCropSelection({ x, y, width, height });
      return;
    }

    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  // Fin de trazo o fin de selección de recorte
  const handleMouseUp = () => {
    if (activeTool === 'crop') {
      setCropDragStart(null);
      return;
    }

    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.closePath();
      ctx.globalAlpha = 1.0;
      pushHistoryState();
    }
  };

  // Aplicar el recorte seleccionado
  const handleApplyCrop = () => {
    if (!cropSelection || cropSelection.width < 10 || cropSelection.height < 10) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = Math.round(cropSelection.width);
    tempCanvas.height = Math.round(cropSelection.height);
    const tempCtx = tempCanvas.getContext('2d');

    tempCtx.drawImage(
      canvas,
      cropSelection.x, cropSelection.y, cropSelection.width, cropSelection.height,
      0, 0, tempCanvas.width, tempCanvas.height
    );

    canvas.width = tempCanvas.width;
    canvas.height = tempCanvas.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(tempCanvas, 0, 0);

    setImageDimensions({ width: canvas.width, height: canvas.height });
    setCropSelection(null);
    setActiveTool('pen');
    pushHistoryState();
  };

  // Guardar y aplicar la imagen editada
  const handleSaveAndClose = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.toBlob(blob => {
      if (!blob) return;
      const originalName = filePreview?.name || 'imagen_editada.png';
      const cleanName = originalName.replace(/\.[^/.]+$/, '') + '_editada.png';
      const newFile = new File([blob], cleanName, { type: 'image/png' });
      const newUrl = URL.createObjectURL(blob);

      onSave(newFile, {
        type: 'image',
        url: newUrl,
        name: cleanName,
        size: blob.size
      });
      onClose();
    }, 'image/png');
  };

  // Cerrar al pulsar Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = e => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Cálculo del cuadro de recorte visible respecto al tamaño en pantalla del canvas
  const getScreenCropStyle = () => {
    if (!cropSelection || !canvasRef.current) return null;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width / canvas.width;
    const scaleY = rect.height / canvas.height;

    return {
      left: `${cropSelection.x * scaleX}px`,
      top: `${cropSelection.y * scaleY}px`,
      width: `${cropSelection.width * scaleX}px`,
      height: `${cropSelection.height * scaleY}px`
    };
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-2 p-md-3"
      style={{
        backgroundColor: 'rgba(15, 23, 42, 0.78)',
        backdropFilter: 'blur(4px)',
        zIndex: 1070
      }}
      onClick={onClose}
    >
      <div
        className="card border-0 shadow-2xl rounded-3 d-flex flex-column overflow-hidden"
        style={{
          width: '96%',
          maxWidth: '860px',
          maxHeight: '92vh',
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          color: isDark ? '#f9fafd' : '#0f172a',
          border: isDark ? '1px solid #233142' : 'none'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Encabezado */}
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2.5 border-bottom flex-shrink-0"
          style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
        >
          <div className="d-flex align-items-center gap-2">
            <span
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
                color: isDark ? '#38bdf8' : '#0284c7'
              }}
            >
              <FontAwesomeIcon icon={faCropAlt} style={{ fontSize: '0.85rem' }} />
            </span>
            <div>
              <h6 className="mb-0 fw-bold" style={{ fontSize: '0.98rem' }}>
                Editar Imagen
              </h6>
              <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                {imageDimensions.width > 0 ? `${imageDimensions.width} × ${imageDimensions.height} px` : 'Cargando imagen...'}
              </small>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-link p-1 text-muted shadow-none"
            onClick={onClose}
            title="Cerrar editor"
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '1.1rem' }} />
          </button>
        </div>

        {/* Barra de Herramientas */}
        <div
          className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 flex-shrink-0"
          style={{
            backgroundColor: isDark ? '#182635' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          {/* Herramientas Principales */}
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-sm px-2.5 py-1 d-flex align-items-center gap-1.5 shadow-none fw-semibold rounded-2"
              style={{
                fontSize: '0.78rem',
                backgroundColor: activeTool === 'pen'
                  ? (isDark ? '#0284c7' : '#0284c7')
                  : 'transparent',
                color: activeTool === 'pen'
                  ? '#ffffff'
                  : (isDark ? '#cbd5e1' : '#475569'),
                border: activeTool === 'pen'
                  ? '1px solid #0284c7'
                  : (isDark ? '1px solid #334155' : '1px solid #cbd5e1')
              }}
              onClick={() => {
                setActiveTool('pen');
                setCropSelection(null);
              }}
              title="Lápiz: dibuja o señala con trazo sólido"
            >
              <FontAwesomeIcon icon={faPen} style={{ fontSize: '0.74rem' }} />
              <span>Lápiz</span>
            </button>

            <button
              type="button"
              className="btn btn-sm px-2.5 py-1 d-flex align-items-center gap-1.5 shadow-none fw-semibold rounded-2"
              style={{
                fontSize: '0.78rem',
                backgroundColor: activeTool === 'highlighter'
                  ? (isDark ? '#eab308' : '#eab308')
                  : 'transparent',
                color: activeTool === 'highlighter'
                  ? '#000000'
                  : (isDark ? '#cbd5e1' : '#475569'),
                border: activeTool === 'highlighter'
                  ? '1px solid #eab308'
                  : (isDark ? '1px solid #334155' : '1px solid #cbd5e1')
              }}
              onClick={() => {
                setActiveTool('highlighter');
                setCropSelection(null);
              }}
              title="Resaltador: dibuja con transparencia tipo marca-textos"
            >
              <span>🖍️ Resaltador</span>
            </button>

            <button
              type="button"
              className="btn btn-sm px-2.5 py-1 d-flex align-items-center gap-1.5 shadow-none fw-semibold rounded-2"
              style={{
                fontSize: '0.78rem',
                backgroundColor: activeTool === 'crop'
                  ? (isDark ? '#059669' : '#059669')
                  : 'transparent',
                color: activeTool === 'crop'
                  ? '#ffffff'
                  : (isDark ? '#cbd5e1' : '#475569'),
                border: activeTool === 'crop'
                  ? '1px solid #059669'
                  : (isDark ? '1px solid #334155' : '1px solid #cbd5e1')
              }}
              onClick={() => setActiveTool('crop')}
              title="Recortar: arrastra sobre la imagen para seleccionar el área"
            >
              <FontAwesomeIcon icon={faCropAlt} style={{ fontSize: '0.74rem' }} />
              <span>Recortar</span>
            </button>

            <button
              type="button"
              className="btn btn-sm px-2 py-1 shadow-none rounded-2"
              style={{
                fontSize: '0.78rem',
                backgroundColor: 'transparent',
                color: isDark ? '#cbd5e1' : '#475569',
                border: isDark ? '1px solid #334155' : '1px solid #cbd5e1'
              }}
              onClick={handleRotate90}
              title="Rotar 90° a la derecha"
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
          </div>

          {/* Selector de color y grosor si usa dibujo */}
          {activeTool !== 'crop' && (
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-1">
                {COLORS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className="rounded-circle p-0 border"
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: c.hex,
                      borderColor: strokeColor === c.hex
                        ? (isDark ? '#38bdf8' : '#0284c7')
                        : (c.id === 'white' ? '#94a3b8' : 'transparent'),
                      boxShadow: strokeColor === c.hex ? '0 0 0 2px rgba(56, 189, 248, 0.5)' : 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => setStrokeColor(c.hex)}
                    title={c.label}
                  />
                ))}
              </div>

              <div className="vr mx-1" style={{ height: '18px', opacity: 0.25 }} />

              <div className="d-flex align-items-center gap-1">
                {STROKE_WIDTHS.map(sw => (
                  <button
                    key={sw.id}
                    type="button"
                    className="btn btn-sm px-1.5 py-0.5 shadow-none"
                    style={{
                      fontSize: '0.70rem',
                      fontWeight: 600,
                      backgroundColor: strokeWidth === sw.size
                        ? (isDark ? '#334155' : '#e2e8f0')
                        : 'transparent',
                      color: isDark ? '#f9fafd' : '#0f172a',
                      border: strokeWidth === sw.size
                        ? (isDark ? '1px solid #64748b' : '1px solid #94a3b8')
                        : '1px solid transparent'
                    }}
                    onClick={() => setStrokeWidth(sw.size)}
                  >
                    {sw.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Historial (Deshacer y Restablecer) */}
          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-sm px-2 py-1 shadow-none rounded-2 d-flex align-items-center gap-1"
              style={{
                fontSize: '0.76rem',
                backgroundColor: 'transparent',
                color: isDark ? '#cbd5e1' : '#475569',
                border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
                opacity: historyIndex <= 0 ? 0.45 : 1
              }}
              disabled={historyIndex <= 0}
              onClick={handleUndo}
              title="Deshacer último cambio"
            >
              <FontAwesomeIcon icon={faUndo} style={{ fontSize: '0.72rem' }} />
              <span>Deshacer</span>
            </button>

            <button
              type="button"
              className="btn btn-sm px-2 py-1 shadow-none rounded-2 text-danger"
              style={{
                fontSize: '0.76rem',
                backgroundColor: 'transparent',
                border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
                opacity: history.length <= 1 ? 0.45 : 1
              }}
              disabled={history.length <= 1}
              onClick={handleReset}
              title="Restablecer imagen original"
            >
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.72rem' }} />
            </button>
          </div>
        </div>

        {/* Zona del Canvas interactivo */}
        <div
          ref={containerRef}
          className="position-relative d-flex align-items-center justify-content-center p-3 overflow-auto flex-grow-1"
          style={{
            minHeight: '380px',
            maxHeight: '62vh',
            backgroundColor: isDark ? '#0b141a' : '#f1f5f9',
            backgroundImage: isDark
              ? 'radial-gradient(#1e293b 1px, transparent 1px)'
              : 'radial-gradient(#cbd5e1 1px, transparent 1px)',
            backgroundSize: '16px 16px',
            userSelect: 'none'
          }}
        >
          <div className="position-relative d-inline-block shadow-lg rounded overflow-hidden">
            <canvas
              ref={canvasRef}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '56vh',
                height: 'auto',
                cursor: activeTool === 'crop' ? 'crosshair' : 'crosshair'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
            />

            {/* Recuadro visual del área a recortar */}
            {activeTool === 'crop' && cropSelection && cropSelection.width > 5 && (
              <div
                className="position-absolute"
                style={{
                  ...getScreenCropStyle(),
                  border: '2px dashed #0284c7',
                  backgroundColor: 'rgba(2, 132, 199, 0.15)',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>

          {/* Botones de acción flotantes para aplicar recorte */}
          {activeTool === 'crop' && cropSelection && cropSelection.width > 15 && cropSelection.height > 15 && (
            <div
              className="position-absolute d-flex align-items-center gap-1.5 p-1.5 rounded-pill shadow-lg"
              style={{
                bottom: '24px',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
                zIndex: 10
              }}
            >
              <button
                type="button"
                className="btn btn-sm btn-success rounded-pill px-3 py-1 shadow-none fw-semibold d-flex align-items-center gap-1"
                style={{ fontSize: '0.78rem' }}
                onClick={handleApplyCrop}
              >
                <FontAwesomeIcon icon={faCheck} />
                <span>Aplicar Recorte</span>
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary rounded-pill px-2.5 py-1 shadow-none"
                style={{ fontSize: '0.78rem' }}
                onClick={() => setCropSelection(null)}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>

        {/* Footer del Modal */}
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2.5 border-top flex-shrink-0"
          style={{
            backgroundColor: isDark ? '#182635' : '#ffffff',
            borderColor: isDark ? '#233142' : '#e2e8f0'
          }}
        >
          <span className="small text-muted" style={{ fontSize: '0.74rem' }}>
            {activeTool === 'crop'
              ? '📐 Arrastra sobre la imagen para seleccionar el área y pulsa "Aplicar Recorte"'
              : '✏️ Arrastra sobre la imagen para dibujar anotaciones o resaltar texto'}
          </span>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm px-3 rounded-pill shadow-none fw-semibold"
              style={{
                fontSize: '0.80rem',
                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#cbd5e1' : '#64748b',
                border: isDark ? '1px solid #334155' : '1px solid #cbd5e1'
              }}
              onClick={onClose}
            >
              Cancelar
            </button>

            <button
              type="button"
              className="btn btn-sm btn-success rounded-pill px-4 shadow-none fw-semibold d-flex align-items-center gap-1.5"
              style={{ fontSize: '0.80rem', backgroundColor: '#059669', borderColor: '#059669' }}
              onClick={handleSaveAndClose}
            >
              <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.75rem' }} />
              <span>Guardar cambios</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ImageEditorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  filePreview: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isDark: PropTypes.bool
};

export default ImageEditorModal;
